/* eslint-disable no-prototype-builtins */
import React, { useMemo } from 'react';
import { CheckedList } from 'modules/Modals/old/FilterModal/components/CheckboxGroup';
import { CollapsedTab } from 'modules/Modals/old/FilterModal/components/CollapsedTab';
import DatePickerGroup from 'modules/Modals/old/FilterModal/components/DatePickerGroup';
import { dropEmptyFilterSections } from 'modules/Modals/old/FilterModal/utils/dropEmptyFilterSections';
import { IsMassAccount, IsVaAccount } from 'modules';
import { useHiddenFilters, FILTER_PAGES } from 'hooks';
import { FILTER_FIELDS } from '_constants/old/filters';
import * as Styled from './styles';
import { VISIT_CHARACTERISTICS_TABS_BASE, VISIT_CHARACTERISTICS_TABS_MASS } from './constant';

// Exposed so VisitsQueryBuilder can tell whether this tab has any visible content and hide the
// whole "Visit Characteristics and Outcomes" tab when it would be empty.
export const useEducationCharacteristicsTabs = () => {
  const isMassAccount = IsMassAccount();
  const isVaAccount = IsVaAccount();
  // P20M-1308 — hide the listed Compliance inspection rows (keeping the unlisted "Violation was
  // found" filter, see below) and the Education and discussion group (which the spec groups with
  // the Other visit details tab) unless the user has the matching permission.
  const { canViewComplianceInspection, canViewEducationDiscussion } = useHiddenFilters(FILTER_PAGES.EDUCATION);

  return useMemo(() => {
    let tabs = VISIT_CHARACTERISTICS_TABS_BASE;

    if (isMassAccount) {
      tabs = tabs.concat(VISIT_CHARACTERISTICS_TABS_MASS);
    }

    if (!isVaAccount) {
      tabs = tabs.filter((tab) => tab.title !== 'Gambling');
    }

    if (!canViewComplianceInspection) {
      // P20M-1308 lists every Compliance inspection row EXCEPT "Violation was found / was not found"
      // (under Compliance and violation). Per the ticket we hide only listed rows, so we keep that
      // one filter visible and drop the rest of the group instead of hiding it wholesale.
      tabs = tabs
        .map((tab) => {
          if (tab.title !== 'Compliance inspection') return tab;
          const complianceAndViolation = tab.collapsed.find((section) => section.title === 'Compliance and violation');
          const keptRows = complianceAndViolation?.collapsed?.filter((row) => row.field === FILTER_FIELDS.VIOLATION);
          if (!keptRows?.length) return null;
          return { ...tab, collapsed: [{ ...complianceAndViolation, collapsed: keptRows }] };
        })
        .filter(Boolean);
    }

    if (!canViewEducationDiscussion) {
      tabs = tabs.filter((tab) => tab.title !== 'Education and discussion' && tab.title !== 'Other visit details');
    }

    return dropEmptyFilterSections(tabs);
  }, [isMassAccount, isVaAccount, canViewComplianceInspection, canViewEducationDiscussion]);
};

function EducationCharacteristics() {
  const VISIT_CHARACTERISTICS_TABS = useEducationCharacteristicsTabs();

  const getElement = (item) => {
    switch (item.type) {
      case 'single':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            <CheckedList name={item.field} options={item.options} />
          </CollapsedTab>
        );
      case 'multiple':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            {item.collapsed.map((elem) => (
              <Styled.FiltersContainer>
                <Styled.FiltersTitle>{elem.title}</Styled.FiltersTitle>
                {elem.collapsed ? (
                  elem.collapsed.map((el) => <CheckedList name={el.field} options={el.options} />)
                ) : (
                  <CheckedList name={elem.field} options={elem.options} />
                )}
              </Styled.FiltersContainer>
            ))}
          </CollapsedTab>
        );
      case 'double':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            {item.collapsed.map((elem) => (
              <CheckedList name={elem.field} options={elem.options} />
            ))}
          </CollapsedTab>
        );
      case 'calendars':
        return (
          <CollapsedTab title={item.title} key={item.id}>
            <DatePickerGroup name={item.field} title="" />
          </CollapsedTab>
        );
      default:
        return null;
    }
  };

  return <Styled.Wrapper>{VISIT_CHARACTERISTICS_TABS.map((item) => getElement(item))}</Styled.Wrapper>;
}

export default EducationCharacteristics;
